import type { ReactNode } from 'react'
import { scaleLinear, scalePoint } from '@visx/scale'
import { Group } from '@visx/group'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { LinePath } from '@visx/shape'
import type { ChartResult, ChartSeriesPoint, ChartSpec } from '../../../types/chart.js'
import { CHART_AXIS_COLOR, CHART_GRID_COLOR, getSeriesStyle } from './chartTheme.js'
import type { ChartOverlayContext } from './chartOverlayTypes.js'
import './chart.css'

export interface BurnChartProps {
  spec: ChartSpec
  data: ChartResult
  width?: number
  height?: number
  className?: string
  testId?: string
  onSeriesClick?: (seriesKey: string) => void
  renderOverlay?: (ctx: ChartOverlayContext) => ReactNode
}

export function BurnChart({
  spec,
  data,
  width = 500,
  height = 240,
  className = '',
  testId,
  onSeriesClick,
  renderOverlay,
}: BurnChartProps) {
  const isBurnUp = spec.style?.burn === 'up'
  const margin = { top: 20, right: 20, bottom: 40, left: 50 }
  const xMax = Math.max(0, width - margin.left - margin.right)
  const yMax = Math.max(0, height - margin.top - margin.bottom)

  const xKeys = Array.from(
    new Set(data.series.flatMap((s) => s.points.map((p) => String(p.x)))),
  )

  const allY = data.series.flatMap((s) => s.points.map((p) => p.y))
  const target = typeof data.target === 'number'
    ? data.target
    : (spec.target?.kind === 'number' ? spec.target.value : null)

  const candidateMax = [...allY, ...(target !== null ? [target] : [])]
  const maxY = Math.max(...candidateMax, 1)

  const xScale = scalePoint<string>({
    domain: xKeys,
    range: [0, xMax],
    padding: 0.5,
  })

  const yScale = scaleLinear<number>({
    domain: [0, maxY],
    range: [yMax, 0],
    nice: true,
  })

  // Ideal line coordinates
  const firstX = xKeys.length > 0 ? (xScale(xKeys[0]) || 0) : 0
  const lastX = xKeys.length > 0 ? (xScale(xKeys[xKeys.length - 1]) || xMax) : xMax

  const idealStartVal = isBurnUp ? 0 : (target ?? (allY.length > 0 ? allY[0] : maxY))
  const idealEndVal = isBurnUp ? (target ?? maxY) : 0

  const idealStartY = yScale(idealStartVal)
  const idealEndY = yScale(idealEndVal)

  return (
    <svg
      width={width}
      height={height}
      className={`chartSvg ${className}`}
      data-testid={testId}
      role="img"
      aria-label={`${spec.title || (isBurnUp ? 'Burnup Chart' : 'Burndown Chart')}`}
    >
      <Group left={margin.left} top={margin.top}>
        {/* Ideal guideline */}
        {xKeys.length > 1 && (
          <line
            x1={firstX}
            y1={idealStartY}
            x2={lastX}
            y2={idealEndY}
            stroke={CHART_AXIS_COLOR}
            strokeWidth={2}
            strokeDasharray="6 4"
            data-testid="chart-burn-ideal-line"
          />
        )}

        {/* Actual series lines */}
        {data.series.map((series, sIdx) => {
          const style = getSeriesStyle(sIdx, spec.style?.seriesColor?.[series.key])
          const seriesKey = series.key || String(sIdx)
          const isInteractive = Boolean(onSeriesClick)

          return (
            <Group
              key={seriesKey}
              tabIndex={isInteractive ? 0 : undefined}
              role={isInteractive ? 'button' : undefined}
              aria-label={isInteractive ? (series.label || seriesKey) : undefined}
              style={{ cursor: isInteractive ? 'pointer' : undefined }}
              onClick={isInteractive ? () => onSeriesClick?.(seriesKey) : undefined}
              onKeyDown={isInteractive ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSeriesClick?.(seriesKey)
                }
              } : undefined}
            >
              <LinePath<ChartSeriesPoint>
                className={isInteractive ? 'band' : undefined}
                data={series.points}
                x={(p) => xScale(String(p.x)) || 0}
                y={(p) => yScale(p.y)}
                stroke={style.color}
                strokeWidth={2.5}
              />
              {series.points.map((point, pIdx) => (
                <circle
                  key={pIdx}
                  cx={xScale(String(point.x)) || 0}
                  cy={yScale(point.y)}
                  r={3.5}
                  fill={style.color}
                />
              ))}
            </Group>
          )
        })}

        {renderOverlay?.({
          xScale,
          yScale,
          margin,
          innerWidth: xMax,
          innerHeight: yMax,
        })}

        {/* Axes */}
        <AxisBottom
          scale={xScale}
          top={yMax}
          stroke={CHART_AXIS_COLOR}
          tickStroke={CHART_GRID_COLOR}
          tickLabelProps={{
            fill: CHART_AXIS_COLOR,
            fontSize: 10,
            textAnchor: 'middle',
          }}
        />
        <AxisLeft
          scale={yScale}
          stroke={CHART_AXIS_COLOR}
          tickStroke={CHART_GRID_COLOR}
          tickLabelProps={{
            fill: CHART_AXIS_COLOR,
            fontSize: 10,
            textAnchor: 'end',
            dx: -4,
            dy: 3,
          }}
        />
      </Group>
    </svg>
  )
}
